// =============================================================================
// ModuleRail — persistent left navigation for desktop, per tenant kind.
//
// Born as ExporterRail (nav audit N6): BigExport was desk-first with 20 routes
// and no persistent navigation, so every lateral move round-tripped a hub.
// That file said "generalise it if a second module ever wants one" — BigPack
// and BigFarm asked, so this is that generalisation. One component; the entry
// list is derived from the ACTIVE TENANT KIND via navModulesFor — the same
// filter the ⌘K palette uses (hasModule + tenant-kind gate) — so a rail entry
// can never lead somewhere the operator is then refused.
//
// FORM. Labels from xl (≥1280px); a 56px icon strip from lg (≥1024px) with the
// name on hover/focus; nothing below that — under lg the dashboard hub,
// BottomNav (touch) and the back button carry navigation exactly as before.
// A half-screen desk window is the case the icon strip exists for.
//
// EXPAND. Under xl the strip's top button widens it to the labelled column,
// and the choice is remembered on that device (`kf49.railExpanded`). Hover
// names alone weren't enough: a farmer on a laptop window just under 1280px
// asked for the toolbar to expand because he couldn't tell the pages apart
// (Frans, Blommelot, 22 Oct). From xl the labels always show, so the button
// hides there.
//
// PINNED. The <nav> stays in flow only to reserve the column's width; the strip
// itself is `fixed inset-y-0` (left = its static position, so it sits where the
// placeholder is). It was a static column before: the shell row is `h-full`,
// the page overflows past it and window-scrolls, so 900px down the rail was
// entirely above the viewport and the left edge was blank (2026-09-08). Sticky
// can't do this — its containing block IS that viewport-tall row. Pinned, the
// strip is never taller than the screen and scrolls itself when the catalogue
// is taller than the window.
//
// PACKHOUSE and FARM get a Home entry because their hub (the dashboard at "/")
// is not a rail module; BigExport's hub IS a rail entry (/exporter →
// Shipments), so Home there would be a duplicate.
//
// ⚠️ Known cost, taken knowingly (from the exporter original): a full-catalogue
// packhouse admin has more entries than a short window shows at once. The
// strip scrolls; per-tenant module entitlements keep real tenants far shorter.
// =============================================================================

import { useLayoutEffect, useRef, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import GuardedNavLink from '@/components/GuardedNavLink'
import { Home, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import clsx from 'clsx'
import { fadeInPage } from '@/lib/motion'
import { pageTransitionActive } from '@/lib/pageTransition'
import { MODULE_CATEGORIES, navModulesFor } from '@/lib/modules'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { useActiveTenant, type TenantKind } from '@/lib/useActiveTenant'
import { usePermissions } from '@/lib/usePermissions'
import { usePersistedState } from '@/hooks/usePersistedState'

// Which tenant kinds get a rail, and how it announces itself to a screen
// reader. Kinds absent here (AGENT, SUPPLIER, …) render nothing — their
// route count doesn't earn a rail yet. TRANSPORTER was in that group until
// 2026-10-02: Jason reversed it for its five pages (ui-flow T1 UF-15), so a
// carrier on a desktop moves between Trips, Availability and Fleet in one click.
const RAIL_KINDS: Partial<Record<TenantKind, { label: string; home: boolean }>> = {
  PACKHOUSE:   { label: 'Packhouse sections', home: true },
  PAYROLL:     { label: 'Payroll sections',   home: true },
  FARM:        { label: 'Farm sections',      home: true },
  EXPORTER:    { label: 'Export sections',    home: false },
  TRANSPORTER: { label: 'Carrier sections',   home: true },
}

/**
 * Which rail entry is "current".
 *
 * Two rules, and the second is the one that bites. A path with no other rail
 * entry nested beneath it matches its own descendants, so /runs still
 * highlights Runs on /runs/<id>. But a path that HAS rail entries nested under
 * it is a hub root — /exporter is Shipments' own path AND the prefix of every
 * exporter page — and matches only itself: treating it as a parent lights up
 * Shipments on all 20 pages at once. Longest match wins where both are
 * eligible.
 *
 * `ownsDescendants` names a path that has a nested rail entry but is a real
 * register, not a hub root: /deliveries carries /deliveries/receive, yet
 * /deliveries/weekly, /deliveries/<id> and its note are still Deliveries.
 * Such a path keeps its descendants; a nested entry still wins by length.
 */
const RAIL_PATHS_OWNING_DESCENDANTS = ['/deliveries']

export function activeRailPath(
  pathname: string,
  paths: string[],
  ownsDescendants: string[] = RAIL_PATHS_OWNING_DESCENDANTS,
): string | null {
  const hasNestedEntry = (p: string) =>
    !ownsDescendants.includes(p) && paths.some(o => o !== p && o.startsWith(p + '/'))
  let best: string | null = null
  for (const p of paths) {
    const hit = pathname === p || (!hasNestedEntry(p) && pathname.startsWith(p + '/'))
    if (hit && (best === null || p.length > best.length)) best = p
  }
  return best
}

function RailLink(
  { path, label, icon: Icon, active, expanded }:
  { path: string; label: string; icon: typeof Home; active: boolean; expanded: boolean },
) {
  return (
    <GuardedNavLink
      to={path}
      title={label}
      aria-current={active ? 'page' : undefined}
      className={clsx(
        'group relative flex items-center gap-2.5 rounded-lg px-2.5 min-h-[36px] text-sm pressable',
        active ? 'surface text-teal-300 font-medium' : 'text-fg-muted hover:bg-surface/60 hover:text-fg',
      )}
    >
      <Icon size={17} className="shrink-0" />
      <span className={clsx(expanded ? 'inline' : 'hidden xl:inline', 'truncate')}>{label}</span>
      {/* Under xl the label is the only thing identifying the icon, so it has to
          be reachable without a pointer — this shows on keyboard focus too.
          Expanded, the label is on screen and the tooltip would repeat it. */}
      <span
        role="tooltip"
        className={clsx(expanded ? 'hidden' : 'xl:hidden', 'pointer-events-none absolute left-full ml-2 z-20 whitespace-nowrap rounded-md surface px-2 py-1 text-xs text-fg opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100')}
      >
        {label}
      </span>
    </GuardedNavLink>
  )
}

type AppMode = 'packhouse' | 'farm'

/**
 * Which app mode the rail should list for, or null to stand down. appMode only
 * matters for FARM: a gFarm route forces 'farm' (it IS the farm side, whatever
 * the dashboard toggle says); the Dashboard mount passes nothing and follows the
 * persisted toggle the tiles use. A farm admin in "packhouse view" gets NO rail
 * — those cross-view packhouse pages are plain g() routes that carry none, so
 * an entry list for them would be a menu to places that lose the menu on
 * arrival. Every other kind ignores the mode.
 */
export function railModeFor(kind: TenantKind | undefined, forced: AppMode | undefined, persisted: AppMode): AppMode | null {
  if (!kind || !RAIL_KINDS[kind]) return null
  const mode = forced ?? persisted
  if (kind === 'FARM' && mode !== 'farm') return null
  return mode
}

/** Modules bucketed by category, in the catalogue's declared operator order; empty categories dropped. */
export function groupByCategory<M extends { category: string }>(mods: M[]): { label: string; modules: M[] }[] {
  return MODULE_CATEGORIES
    .map(cat => ({ label: cat.label, modules: mods.filter(m => m.category === cat.key) }))
    .filter(g => g.modules.length > 0)
}

type RailGroup = { label: string; modules: { id: string; path: string; label: string; icon: typeof Home }[] }

/** The rail's markup — hook-free, so it can be rendered straight from a test. */
export function RailView(
  { label, home, groups, active, pathname, expanded = false, onToggleExpanded }:
  {
    label: string; home: boolean; groups: RailGroup[]; active: string | null; pathname: string
    /** Under xl: show the labelled column instead of the icon strip. */
    expanded?: boolean
    /** Omitted → no expand button (the strip stays as it was). */
    onToggleExpanded?: () => void
  },
) {
  // Expanded is xl's labelled form at every rail width.
  const width = expanded ? 'w-52' : 'w-14 xl:w-52'
  return (
    <nav
      aria-label={label}
      // `module-rail` is the hook index.css uses to hide this under pointer:coarse —
      // a touch laptop or landscape iPad at ≥lg keeps BottomNav, not both.
      // Width placeholder only — the pinned panel below carries the strip.
      className={clsx('module-rail hidden lg:block shrink-0 print:hidden', width)}
    >
    <div className={clsx('scrollbar-thin-dark fixed inset-y-0 flex flex-col overflow-y-auto overscroll-contain border-r hairline surface-sunk px-2', width,
      expanded ? 'gap-4 py-3' : 'gap-3 xl:gap-4 py-2 xl:py-3')}>
      {onToggleExpanded && (
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          title={expanded ? 'Hide page names' : 'Show page names'}
          className="xl:hidden flex items-center gap-2.5 rounded-lg px-2.5 min-h-[36px] text-sm text-fg-muted hover:bg-surface/60 hover:text-fg transition-colors"
        >
          {expanded ? <PanelLeftClose size={17} className="shrink-0" /> : <PanelLeftOpen size={17} className="shrink-0" />}
          <span className={expanded ? 'truncate' : 'sr-only'}>{expanded ? 'Hide page names' : 'Show page names'}</span>
        </button>
      )}
      {home && (
        <div className="flex flex-col gap-0.5">
          <RailLink path="/" label="Home" icon={Home} active={pathname === '/'} expanded={expanded} />
        </div>
      )}
      {groups.map(g => (
        <div key={g.label} role="group" aria-label={g.label} className="flex flex-col gap-0.5">
          {/* The category name is meaningless as a 56px stub, so under xl it
              becomes a rule — the grouping still reads, the noise doesn't. */}
          <div className={clsx(expanded ? 'block' : 'hidden xl:block', 'px-2.5 pb-1 text-[10px] font-medium uppercase tracking-wider text-muted')}>
            {g.label}
          </div>
          {/* …and in icon mode the rule itself carries the name: an 8px hover
              box, so the group is nameable without being a label. */}
          <div className={clsx(expanded ? 'hidden' : 'xl:hidden', 'mx-2 mb-0.5 h-2 border-t hairline')} title={g.label} />
          {g.modules.map(m => <RailLink key={m.id} path={m.path} label={m.label} icon={m.icon} active={active === m.path} expanded={expanded} />)}
        </div>
      ))}
    </div>
    </nav>
  )
}

function ModuleRail({ appMode: forced }: { appMode?: AppMode }) {
  const { pathname } = useLocation()
  const { data: me } = useCurrentUser()
  const { data: tenant } = useActiveTenant()
  const { perms } = usePermissions()
  // Hook order: read this BEFORE the kind gate below, or the hook count changes
  // the moment the tenant query resolves.
  const [persistedMode] = usePersistedState<AppMode>('kf49.appMode', 'packhouse')
  const [expanded, setExpanded] = usePersistedState<boolean>('kf49.railExpanded', false)

  const kind = tenant?.kind
  const appMode = railModeFor(kind, forced, persistedMode)
  const cfg = kind && RAIL_KINDS[kind]
  if (!appMode || !cfg) return null

  const mods = navModulesFor(me ?? null, { kind, enabledModules: tenant?.enabledModules, appMode, permMap: perms, slug: tenant?.slug })
  // A rail with one destination is furniture, not navigation.
  if (mods.length < 2) return null

  return (
    <RailView
      label={cfg.label}
      home={cfg.home}
      groups={groupByCategory(mods)}
      active={activeRailPath(pathname, mods.map(m => m.path))}
      pathname={pathname}
      expanded={expanded}
      onToggleExpanded={() => setExpanded(e => !e)}
    />
  )
}

// RailShell — puts <ModuleRail/> beside a routed page. Mounted once inside the
// per-kind route wrappers (gPack / gFarm / gLabour / gExporter in App.tsx) and
// on the Dashboard route, so the rail never disappears on Home and the layout
// stops jumping sideways on every hub round-trip. Not per page: every gated route already passes through those
// wrappers, so they are the modules' only layout seam and no page needs to
// know the rail exists.
//
// The page keeps owning its own scrolling. `min-w-0` on the content column is
// load-bearing — without it a wide table or diagram inside a page refuses to
// shrink (flex items default to min-width:auto) and pushes the whole layout
// sideways, rail included.
//
// ⚠️ NO `min-h-full` ON THESE TWO WRAPPERS. It is redundant next to `h-full`
// (height:100% already fixes the height, so min-height:100% can never bind),
// and it is actively harmful: `body.has-bottom-nav .min-h-full` in index.css
// reserves the bottom bar's 44px on ANY element carrying the class, because it
// was written for PAGE ROOTS that grow and window-scroll. These two wrappers
// are structural, so the reservation was being applied three times over — once
// on <body>, once here, once on the content column.
//
// Measured in-browser at a 393×852 viewport: the page's scroll container ended
// 132px above the viewport bottom instead of the 44px the nav actually
// occupies, leaving 88px of dead space under every list on every touch device,
// on every page inside this shell. Removing the two classes puts it back to 44.
//
// `page-slot` on the content column is the hook index.css reserves the bottom
// bar on — on its CHILD, the page root, whatever classes that root carries, so
// a page that forgot `min-h-full` still ends clear of the bar (farm audit X-08:
// four registers had their last row under it). Never on the column itself:
// the column is h-full, and a reservation there would shrink every bounded
// page a second time, the defect described above.
//
// The slot also fades each new page in (src/lib/motion.ts). Keyed on the
// PATHNAME only: a filter or an open record held in the search params is the
// same page and must not flicker. A layout effect, so the fade starts before
// the new page's first paint instead of flashing it at full opacity first.
// Where the browser runs a View Transition slide (src/lib/pageTransition.ts)
// the slide replaces the fade, never both.
// The kiosk routes (/clock-face, /device-capture) never mount this shell.
export function RailShell({ children, appMode }: { children: ReactNode; appMode?: AppMode }) {
  const slotRef = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()
  useLayoutEffect(() => { if (!pageTransitionActive()) fadeInPage(slotRef.current) }, [pathname])
  return (
    <div className="flex h-full">
      <ModuleRail appMode={appMode} />
      <div ref={slotRef} className="page-slot flex-1 min-w-0 h-full">{children}</div>
    </div>
  )
}
